/* eslint-disable react/forbid-prop-types */
import React, { useState, memo, useContext, useRef } from 'react';
import { makeStyles } from 'tss-react/mui';
import PropTypes from 'prop-types';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Close';
import Grid from '../../../../components/Grid/Grid';
import Typography from '../../../../components/Typography/Typography';
import useForm from '../../../../hooks/useForm';
import IconButton from '../../../../components/IconButton/IconButton';
import InputField from '../../../../components/InputField/InputField';
import useCancelToken from '../../../../hooks/useCancelToken';
import {
  deletePowderBatchTemperatureConfig,
  editPowderBatchTemperatureConfig,
} from '../../../../services/settings-config';
import { DialogContext } from '../../../../contexts/DialogContext';
import { dialogActions } from '../../../../reducers/dialogReducer';
import CircularProgress from '../../../../components/CircularProgress/CircularProgress';

const useStyles = makeStyles()((theme) => ({
  iconBtn: {
    width: 26,
    height: 26,
    border: '1px solid',
    marginLeft: theme.spacing(1.25),
    marginTop: 0,
    marginBottom: 0,
  },
  iconBtnIcon: {
    padding: 3.9,
  },
  iconBtnEdit: {
    color: theme.palette.primary.main,
  },
  iconBtnDelete: {
    color: theme.palette.error.main,
  },
  iconBtnSave: {
    color: theme.palette.secondary.main,
  },
  iconBtnCancel: {
    color: theme.palette.grey[600],
  },
}));

const PowderBatchTemperatureConfigRow = ({
  powderBatch,
  configRow,
  onSaveTemperatureConfig,
  onCancelEditTemperatureConfig,
  onDeleteTemperatureConfigClick,
  onDeleteTemperatureConfig,
  onDeleteTemperatureConfigError,
  onEditTemperatureConfig,
  isUnderEditMode,
  onSaveTemperatureConfigError,
}) => {
  const { classes } = useStyles();
  const { diameter, temperature, rowId } = configRow;
  const { newCancelToken } = useCancelToken();
  const [isEditMode, setIsEditMode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const initialForm = {
    diameter,
    temperature,
  };
  const { formData, setFormData, formErr, setFormErr, resetFormErr } = useForm(initialForm);
  const originalFormData = useRef(initialForm);
  const { dialogDispatch } = useContext(DialogContext);

  const validationForm = () => {
    if (
      formData.temperature.length === 0 ||
      Number.isNaN(Number(formData.temperature)) ||
      Number(formData.temperature) <= 0
    ) {
      setFormErr({ ...formErr, temperature: 'Temperature須為正數' });
      return false;
    }
    return true;
  };

  const handleEditTemperatureConfig = () => {
    setIsEditMode(true);
    originalFormData.current = {
      diameter: Number(formData.diameter),
      temperature: Number(formData.temperature),
    };
    onEditTemperatureConfig();
  };

  const handleCancelEditTemperatureConfig = () => {
    setIsEditMode(false);
    setFormData(originalFormData.current);
    onCancelEditTemperatureConfig();
  };

  const handleSaveTemperatureConfig = () => {
    if (!validationForm()) return;
    resetFormErr();
    dialogDispatch({
      type: dialogActions.OPEN,
      config: {
        title: '是否確定要修改此筆溫度設定？',
        message: '將會影響已存在的粉料檢測數據，請確認是否要修改資料？',
        handleConfirm: async () => {
          const cancelToken = newCancelToken();
          setIsSaving(true);
          await editPowderBatchTemperatureConfig({
            data: {
              deviceId: Number(powderBatch),
              size: Number(formData.diameter),
              temperature: Number(formData.temperature),
            },
            cancelToken,
            onSuccess: () => {
              setIsEditMode(false);
              setFormData({
                diameter: Number(formData.diameter),
                temperature: Number(formData.temperature),
              });
              onSaveTemperatureConfig(formData);
            },
            onError: (result, _rtCode, noPrivilegeMsg) => {
              onSaveTemperatureConfigError(result, noPrivilegeMsg);
            },
            onSettled: () => {
              setIsSaving(false);
            },
          });
        },
      },
    });
  };

  const handleDeleteTemperatureConfig = async () => {
    const cancelToken = newCancelToken();
    setIsDeleting(true);
    await deletePowderBatchTemperatureConfig({
      data: {
        deviceId: Number(powderBatch),
        size: Number(formData.diameter),
      },
      cancelToken,
      onSuccess: () => {
        onDeleteTemperatureConfig(rowId);
      },
      onError: (result, _rtCode, noPrivilegeMsg) => {
        onDeleteTemperatureConfigError(result, noPrivilegeMsg);
      },
      onSettled: () => {
        setIsDeleting(false);
      },
    });
  };

  const handleDeleteTemperatureConfigClick = () => {
    dialogDispatch({
      type: dialogActions.OPEN,
      config: {
        title: '是否確定要刪除此筆溫度設定？',
        message: `是否確認刪除試燒環 [${powderBatch}] 於 [${formData.diameter}] 的溫度設定？按下確認後，資料將無法復原。`,
        handleConfirm: () => {
          onDeleteTemperatureConfigClick();
          handleDeleteTemperatureConfig();
        },
      },
    });
  };

  return (
    <Grid
      container
      item
      xs={12}
      alignItems="center"
      sx={(theme) => ({
        borderBottom: `1px solid ${theme.palette.divider}`,
        padding: 0,
        marginBottom: 0.6,
      })}
    >
      <Grid item xs={3}>
        <Typography variant="body1">{formData.diameter}</Typography>
      </Grid>
      <Grid item xs={7}>
        {isEditMode ? (
          <InputField
            size="small"
            value={formData.temperature}
            onChange={(e) => setFormData({ ...formData, temperature: e.target.value })}
            name="temperature"
            type="number"
            error={formErr.temperature}
            errormsg={formErr.temperature}
            required
            disabled={isSaving}
          />
        ) : (
          <Typography variant="body1">{formData.temperature}</Typography>
        )}
      </Grid>
      <Grid item container justifyContent="flex-end" alignItems="center" xs={2}>
        {isEditMode ? (
          <>
            {isSaving ? (
              <CircularProgress id="saveLoading" size={26} />
            ) : (
              <IconButton
                className={`${classes.iconBtn} ${classes.iconBtnSave}`}
                aria-label="save"
                onClick={handleSaveTemperatureConfig}
              >
                <SaveIcon className={classes.iconBtnIcon} />
              </IconButton>
            )}
            <IconButton
              className={`${classes.iconBtn} ${classes.iconBtnCancel}`}
              aria-label="cancel"
              onClick={handleCancelEditTemperatureConfig}
              disabled={isSaving}
            >
              <CancelIcon className={classes.iconBtnIcon} />
            </IconButton>
          </>
        ) : (
          <>
            <IconButton
              className={`${classes.iconBtn} ${classes.iconBtnEdit}`}
              aria-label="edit"
              onClick={handleEditTemperatureConfig}
              disabled={isUnderEditMode}
            >
              <EditIcon className={classes.iconBtnIcon} />
            </IconButton>
            {isDeleting ? (
              <CircularProgress
                id="saveLoading"
                size={26}
                sx={(theme) => ({ marginLeft: theme.spacing(1) })}
              />
            ) : (
              <IconButton
                className={`${classes.iconBtn} ${classes.iconBtnDelete}`}
                aria-label="delete"
                onClick={handleDeleteTemperatureConfigClick}
                disabled={isUnderEditMode}
              >
                <DeleteIcon className={classes.iconBtnIcon} />
              </IconButton>
            )}
          </>
        )}
      </Grid>
    </Grid>
  );
};

PowderBatchTemperatureConfigRow.propTypes = {
  configRow: PropTypes.object.isRequired,
  powderBatch: PropTypes.string.isRequired,
  onSaveTemperatureConfig: PropTypes.func.isRequired,
  onDeleteTemperatureConfigClick: PropTypes.func.isRequired,
  onDeleteTemperatureConfig: PropTypes.func.isRequired,
  onDeleteTemperatureConfigError: PropTypes.func.isRequired,
  onEditTemperatureConfig: PropTypes.func.isRequired,
  onCancelEditTemperatureConfig: PropTypes.func.isRequired,
  isUnderEditMode: PropTypes.bool.isRequired,
  onSaveTemperatureConfigError: PropTypes.func.isRequired,
};

export default memo(PowderBatchTemperatureConfigRow);
